import React from 'react';
import { BenefitCard } from 'components/BenefitCard/BenefitCard';
import { BenefitDetailPage } from 'components/BenefitDetailPage/BenefitDetailPage';
import { useDeviceType } from 'contexts/ThemeContext/hooks/useDeviceType';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { useFeatureFlags } from 'features/DeveloperTools/hooks/useFeatureFlags';
import { FeatureFlagKeys } from 'features/DeveloperTools/config/constants';
import { useBenefits } from 'features/resources/hooks';
import { toBenefitDetail } from 'features/resources/utils/benefitsUtils';
import { ResourceCardGrid, ResourceCardGridCell } from './ResourceCardGrid';
import { ResourceSection } from './ResourceSection';

// The preview row fits one fewer card on the narrower mobile/tablet layouts.
const COMPACT_BENEFIT_LIMIT = 4;
const DESKTOP_BENEFIT_LIMIT = 5;

export const ResourceBenefitSection = () => {
  const { isDesktop } = useDeviceType();
  const { translation } = useTranslationContext();
  const { isEnabled } = useFeatureFlags();
  const isBenefitsFeatureEnabled = isEnabled(FeatureFlagKeys.BENEFITS_FEATURE);

  const previewLimit = isDesktop ? DESKTOP_BENEFIT_LIMIT : COMPACT_BENEFIT_LIMIT;

  // One extra benefit is requested to detect that more exist than the preview shows
  const {
    benefits,
    isLoading,
    isError,
    selectedBenefit,
    isModalOpen,
    closeModal,
    handleViewAll,
    handleBenefitCardClick,
  } = useBenefits({
    pageSize: previewLimit + 1,
    skip: !isBenefitsFeatureEnabled,
  });

  const previewBenefits = benefits.slice(0, previewLimit);

  if (!isBenefitsFeatureEnabled || isLoading || isError || previewBenefits.length === 0) {
    return null;
  }

  // "View all" is only shown when there are more benefits
  const showViewAll = benefits.length > previewLimit;

  const title = translation('resources_benefits_header');
  const viewAllText = translation('member_resources_view_all_benefits_button');

  return (
    <>
      <ResourceSection
        title={title}
        titleId="benefits_section"
        description={translation('resources_benefits_subheader')}
        viewAllText={viewAllText}
        viewAllAriaLabel={`${viewAllText} - ${title}`}
        onViewAll={handleViewAll}
        showViewAll={showViewAll}
      >
        <ResourceCardGrid>
          {previewBenefits.map((benefit) => (
            <ResourceCardGridCell key={benefit.id}>
              <BenefitCard
                title={benefit.title}
                imageUrl={benefit.thumbnailImageUrl}
                backgroundColor="grey.400"
                ariaLabel={benefit.title}
                selected={selectedBenefit?.id === benefit.id}
                onClick={() => handleBenefitCardClick(benefit)}
              />
            </ResourceCardGridCell>
          ))}
        </ResourceCardGrid>
      </ResourceSection>
      <BenefitDetailPage
        benefit={selectedBenefit ? toBenefitDetail(selectedBenefit) : undefined}
        open={isModalOpen}
        onClose={closeModal}
      />
    </>
  );
};
